import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

interface FloatingMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  minWidth?: number;
  align?: 'start' | 'end';
}

export function FloatingMenu({ anchorEl, open, onClose, children, minWidth = 96, align = 'end' }: FloatingMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const place = useCallback(() => {
    if (!anchorEl || !menuRef.current) return;
    const rect = anchorEl.getBoundingClientRect();
    const menu = menuRef.current;
    const menuH = menu.offsetHeight;
    if (align === 'start') {
      menu.style.right = '';
      menu.style.left = String(Math.max(8, Math.min(rect.left, window.innerWidth - menu.offsetWidth - 8))) + 'px';
    } else {
      menu.style.left = '';
      menu.style.right = String(window.innerWidth - rect.right) + 'px';
    }
    if (window.innerHeight - rect.bottom < menuH + 8) {
      menu.style.top = '';
      menu.style.bottom = `${window.innerHeight - rect.top + 6}px`;
    } else {
      menu.style.bottom = '';
      menu.style.top = `${rect.bottom + 6}px`;
    }
  }, [anchorEl, align]);

  useLayoutEffect(() => {
    if (!open) {
      setVisible(false);
      return;
    }
    setVisible(false);
    requestAnimationFrame(() => {
      place();
      setVisible(true);
    });
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const handleDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (anchorEl?.contains(e.target as Node)) return;
      onClose();
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open, anchorEl, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        zIndex: 9999,
        minWidth,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        boxShadow: '0 10px 24px rgba(0,0,0,.28)',
        padding: 6,
        top: -9999,
        right: 0,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.08s',
      }}
    >
      {children}
    </div>,
    document.body
  );
}
